import { useState } from "react";

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
}

/**
 * Expand/collapse chevron (Feather `chevron-right`). Points right when collapsed
 * and is rotated 90° by CSS when expanded, so the state change animates instead
 * of swapping glyphs. `currentColor` makes it inherit the selected row's white.
 * SVG rather than a Unicode triangle: the glyph rendered at whatever weight and
 * baseline the resolved font happened to use.
 */
function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

interface TreeNodeItemProps {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  autoExpand: boolean;
}

function TreeNodeItem({ node, depth, selectedId, onSelect, autoExpand }: TreeNodeItemProps) {
  // `automatic_views_expansion` (Mviews.ini, default on): when set, every node
  // starts expanded (C++ view_splitter auto-expands the group/turbine nodes);
  // when off the tree starts collapsed and the user expands manually.
  const [expanded, setExpanded] = useState(autoExpand);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className={`tree-node${selectedId === node.id ? " selected" : ""}`}
        style={{ paddingLeft: depth * 16 + 4 }}
        onClick={() => {
          if (hasChildren) {
            setExpanded(!expanded);
          } else {
            onSelect(node.id);
          }
        }}
      >
        {hasChildren ? (
          <span
            className={`tree-toggle${expanded ? " expanded" : ""}`}
            aria-hidden="true"
            data-testid="tree-toggle"
          >
            <ChevronIcon />
          </span>
        ) : (
          // Keeps leaf labels aligned with their expandable siblings.
          <span className="tree-toggle-spacer" />
        )}
        <span className="tree-label">{node.label}</span>
      </div>
      {hasChildren && expanded && node.children!.map((child) => (
        <TreeNodeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          autoExpand={autoExpand}
        />
      ))}
    </div>
  );
}

interface TreeViewProps {
  nodes: TreeNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** From `automatic_views_expansion` (Mviews.ini); defaults to expanded. */
  autoExpand?: boolean;
}

export function TreeView({ nodes, selectedId, onSelect, autoExpand = true }: TreeViewProps) {
  return (
    <div className="tree-view">
      {nodes.map((node) => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          autoExpand={autoExpand}
        />
      ))}
    </div>
  );
}
